import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAuth, useGeneration } from "@/app";
import { usePlayer } from "@/app/providers/PlayerProvider";
import { registerTopUpModalOpener } from "@/shared/lib/topUpModalBridge";
import { sidebarTranslations } from "@/shared/locales";
import { IconChartBar, IconHome, IconMusic, IconPlus, IconWallet } from "@tabler/icons-react";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./MiniAppNavbar.module.scss";
export const MiniAppNavbar = ({ language, isAuthenticated, openLoginModal, openRegisterModal, }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isGuest } = useAuth();
    const { isGeneratingText } = useGeneration();
    const { isPlayerOpen } = usePlayer();
    const t = sidebarTranslations[language];
    const navRef = useRef(null);
    useEffect(() => {
        // Гость тоже может пополнять баланс (гостевой чекаут), поэтому opener
        // регистрируем и ему. Раньше условие было `!isAuthenticated` — а у гостя
        // isAuthenticated=false, так что opener занулялся, и при 402 (не хватает
        // средств) на мобилке модалка/переход к оплате НЕ всплывали.
        if (!isAuthenticated && !isGuest) {
            registerTopUpModalOpener(null);
            return;
        }
        registerTopUpModalOpener(() => {
            navigate("/balance");
        });
        return () => registerTopUpModalOpener(null);
    }, [isAuthenticated, isGuest, navigate]);
    // Публикуем фактическую высоту навбара в CSS-переменную, чтобы плеер и
    // прилипающие кнопки/инпуты вставали вплотную к нему при любой ширине
    // (подписи могут переноситься на две строки → высота меняется).
    useEffect(() => {
        const el = navRef.current;
        if (!el)
            return;
        const sync = () => {
            document.documentElement.style.setProperty("--miniapp-navbar-h", `${el.offsetHeight}px`);
        };
        sync();
        // ВАЖНО: наблюдаем border-box, а не content-box (по умолчанию). Высота
        // навбара растёт за счёт padding-bottom с safe-area (env(safe-area-inset-
        // bottom)); при изменении safe-area (айфон: сворачивание панели Safari,
        // поворот) меняется именно padding — content-box не меняется, и
        // content-box-обсёрвер НЕ срабатывает → переменная зависала на старом
        // значении, а прилипающие элементы наезжали на навбар / оставляли зазор.
        const ro = new ResizeObserver(sync);
        ro.observe(el, { box: "border-box" });
        window.addEventListener("resize", sync);
        // Сворачивание нижней панели iOS Safari меняет safe-area, но не всегда
        // шлёт window.resize — visualViewport ловит это надёжнее.
        window.visualViewport?.addEventListener("resize", sync);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", sync);
            window.visualViewport?.removeEventListener("resize", sync);
            document.documentElement.style.removeProperty("--miniapp-navbar-h");
        };
    }, []);
    const handleMyClick = () => {
        // Гость — полноценный пользователь (гостевой чекаут): пускаем в «Мои песни».
        if (isAuthenticated || isGuest) {
            navigate("/my-songs");
        }
        else {
            // Для неавторизованных пользователей открываем модальное окно авторизации
            if (openLoginModal) {
                openLoginModal();
            }
            else {
                navigate("/");
            }
        }
    };
    const handleBalanceClick = () => {
        // Гость может пополнять баланс — пускаем на /balance.
        if (isAuthenticated || isGuest) {
            navigate("/balance");
        }
        else {
            // Для неавторизованных пользователей открываем модальное окно авторизации
            if (openLoginModal) {
                openLoginModal();
            }
            else {
                navigate("/");
            }
        }
    };
    const isActive = (path) => {
        if (path === "/") {
            return location.pathname === "/";
        }
        return location.pathname.startsWith(path);
    };
    const isGenerateSongActive = () => {
        return location.pathname === "/generate-song";
    };
    const navItems = [
        {
            path: "/",
            icon: IconHome,
            label: language === "ru" ? "Главная" : "Home",
            isActive: isActive("/"),
            onClick: () => navigate("/"),
        },
        {
            path: "/my-songs",
            icon: IconMusic,
            // В узком таб-баре «Мои песни» в два слова рвётся уродливо.
            // В десктопном сайдбаре остаётся длинная подпись (`t.mySongs`).
            label: language === "ru" ? "Песни" : "Songs",
            isActive: location.pathname.startsWith("/my-"),
            onClick: handleMyClick,
        },
        {
            path: "/generate-song",
            icon: IconPlus,
            label: t.create,
            isActive: isGenerateSongActive(),
            onClick: () => navigate("/generate-song"),
        },
        {
            path: "/balance",
            icon: IconWallet,
            label: t.balance,
            isActive: isActive("/balance"),
            onClick: handleBalanceClick,
        },
        {
            path: "/charts",
            icon: IconChartBar,
            label: language === "ru" ? "Чарты" : "Charts",
            isActive: isActive("/charts"),
            onClick: () => navigate("/charts"),
        },
    ];
    return (_jsx(_Fragment, { children: _jsx("div", { ref: navRef, className: `${styles.navbar}${isPlayerOpen ? ` ${styles.navbarPlayerOpen}` : ""}`, style: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-around",
                gap: 0,
            }, children: navItems.map((item) => {
                const IconComponent = item.icon;
                return (_jsx("div", { onClick: isGeneratingText ? undefined : item.onClick, className: `${styles.navButton} ${item.isActive ? styles.navButtonActive : ""} ${isGeneratingText ? styles.navButtonDisabled : ""}`, children: _jsxs("div", { style: {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                        }, children: [_jsx(IconComponent, { size: 18, className: styles.navIcon, style: {
                                    color: item.isActive
                                        ? "var(--vp-brand)"
                                        : "var(--vp-text-dim)",
                                } }), _jsx("span", { className: styles.navLabel, children: item.label })] }) }, item.path));
            }) }) }));
};
